import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Target, Heart, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { portfolioImages, stats } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion for Design',
      description: 'Every space we create is infused with our love for aesthetics and attention to detail.'
    },
    {
      icon: Users,
      title: 'Client-Centric Approach',
      description: 'Your vision, lifestyle, and preferences are at the heart of every design decision.'
    },
    {
      icon: Target,
      title: 'Precision & Excellence',
      description: 'We maintain the highest standards in craftsmanship and execution.'
    },
    {
      icon: Award,
      title: 'Timeless Design',
      description: 'We create spaces that remain beautiful and functional for years to come.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#F5F0E8]">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#FDFBF7] -skew-x-12 origin-top-right" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 animate-fade-in">
                About Us
              </span>
              <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-[#2C2C2C] mb-6 leading-tight animate-fade-in-up">
                Crafting Spaces That
                <span className="block text-[#B87333]">Tell Your Story</span>
              </h1>
              <div className="divider-copper mb-8 animate-fade-in-up delay-200" />
              <p className="text-[#6B6B6B] text-lg leading-relaxed mb-6 animate-fade-in-up delay-300">
                At InArch Design Studio, we believe that great design goes beyond 
                aesthetics. It's about creating environments that resonate with 
                the people who inhabit them — spaces that feel like home from the 
                very first moment.
              </p>
              <p className="text-[#6B6B6B] leading-relaxed animate-fade-in-up delay-400">
                Founded in 2012, we have grown from a small design firm to one of 
                Navi Mumbai's most trusted interior design studios, but our core 
                philosophy remains unchanged: every space has a story waiting to be told.
              </p>
            </div>
            <div className="relative animate-fade-in-up delay-500">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={portfolioImages.livingRooms[0]}
                  alt="InArch Design Studio"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#B87333] p-8 shadow-2xl hidden lg:block">
                <div className="font-serif text-5xl text-white mb-1">20+</div>
                <div className="text-white/80 text-sm tracking-wider uppercase">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
              Our Philosophy
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
              Beauty Meets Functionality
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed">
              We don't just design spaces — we create environments that inspire, 
              connect, and enhance daily living. Our approach balances aesthetic 
              elegance with practical functionality, ensuring every design serves 
              both the eye and the lifestyle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white border border-[#E8E3DB] hover:border-[#B87333]/30 transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#B87333]/30 bg-[#B87333]/5 mb-6 group-hover:bg-[#B87333] group-hover:border-[#B87333] transition-all duration-300">
                  <value.icon size={28} className="text-[#B87333] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-24 lg:py-32 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
                Our Approach
              </span>
              <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
                A Personal Journey,
                <span className="block text-[#D4956A]">Not Just a Project</span>
              </h2>
              <div className="divider-copper mb-8" />
              <p className="text-[#9A9A9A] text-lg leading-relaxed mb-8">
                Every project at InArch begins with understanding — not just your 
                spatial needs, but your dreams, routines, and the moments that 
                matter most to you. This deep understanding forms the foundation 
                of our design process.
              </p>
              <ul className="space-y-4">
                {[
                  'Comprehensive consultation to understand your vision',
                  'Detailed space planning and conceptual designs',
                  'Material and finish selection with your input',
                  'Seamless execution with minimal disruption',
                  'Post-completion support and styling assistance'
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle size={20} className="text-[#B87333] mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-[#9A9A9A]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={portfolioImages.bedrooms[0]}
                    alt="Design Process"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={portfolioImages.kitchens[1]}
                    alt="Design Excellence"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-24 lg:py-32 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={portfolioImages.offices[0]}
                  alt="Commercial Projects"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
                Our Experience
              </span>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
                Diverse Expertise,
                <span className="block text-[#B87333]">Unified Excellence</span>
              </h2>
              <div className="divider-copper mb-8" />
              <p className="text-[#6B6B6B] text-lg leading-relaxed mb-6">
                From cozy apartments to expansive villas, from boutique offices to 
                hospitality spaces — our portfolio spans the full spectrum of interior 
                design. This diverse experience enriches every project we undertake.
              </p>
              <p className="text-[#6B6B6B] leading-relaxed mb-8">
                Our team brings together expertise in residential and commercial design, 
                allowing us to approach each project with fresh perspectives while 
                maintaining the highest standards of quality and craftsmanship.
              </p>
              <Link
                to="/gallery"
                className="inline-flex items-center px-8 py-4 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-0.5 group"
              >
                Explore Our Work
                <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#B87333]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-serif text-5xl lg:text-6xl text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm tracking-wider uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
            Let's Create Together
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-10 max-w-2xl mx-auto">
            Every great space begins with a conversation. Let's discuss your vision 
            and explore how we can bring it to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/918591613610"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-5 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-0.5 group"
            >
              Get in Touch
              <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-10 py-5 border border-[#B87333] text-[#B87333] text-sm tracking-wider uppercase hover:bg-[#B87333] hover:text-white transition-all duration-300"
            >
              Our Services
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
